import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { Badge, type Column, Heading, Paragraph, Table } from "@fronterahealth/frontera-ui-components";

import { type ExpertChatOversightRow, useListExpertChatProviders, useListExpertChats } from "@api/rest/types-and-hooks";
import { TableFilterDropdown } from "@components/TableFilterDropdown/TableFilterDropdown";

import { ReassignChatDialog } from "./ReassignChatDialog";
import {
  EXPERT_CHATS_PAGE_SIZE,
  EXPERT_CHATS_PAGE_SIZE_OPTIONS,
  EXPERT_CHAT_STATUS_LABELS,
  ExpertChatStatus,
  type ExpertChatStatusValue,
  PROVIDER_STALE_TIME_MS,
  SEARCH_DEBOUNCE_MS,
  STATUS_FILTER_ITEMS,
  UNASSIGNED_PROVIDER_LABEL,
} from "./constants";

const renderStatus = (record: ExpertChatOversightRow) => {
  if (record.needs_review) return <Badge appearance="warn" text="Needs review" />;
  if (record.status === ExpertChatStatus.FINISHED) {
    return <Badge appearance="neutral" text={EXPERT_CHAT_STATUS_LABELS[ExpertChatStatus.FINISHED]} />;
  }
  return <Badge appearance="success" text={EXPERT_CHAT_STATUS_LABELS[ExpertChatStatus.OPEN]} />;
};

const COLUMNS: Column<ExpertChatOversightRow>[] = [
  {
    title: "Client",
    dataIndex: "learner_name",
    key: "learner_name",
    render: (value, record) => (
      <div className="flex items-center gap-2">
        {record.is_unread && <span className="h-2 w-2 shrink-0 rounded-full bg-globemallow-600" aria-label="Unread" />}
        <Paragraph size="small">{(value as string) || "—"}</Paragraph>
      </div>
    ),
  },
  {
    title: "Caregiver",
    dataIndex: "caregiver_name",
    key: "caregiver_name",
    render: (value, record) => (
      <div className="flex flex-col">
        <Paragraph size="small">{(value as string) || "—"}</Paragraph>
        {record.caregiver_email && (
          <Paragraph size="small" colorType="secondary">
            {record.caregiver_email}
          </Paragraph>
        )}
      </div>
    ),
  },
  {
    title: "Assigned expert",
    dataIndex: "assigned_provider_name",
    key: "assigned_provider_name",
    render: (value) => <Paragraph size="small">{(value as string) || UNASSIGNED_PROVIDER_LABEL}</Paragraph>,
  },
  {
    title: "Last activity",
    dataIndex: "last_activity",
    key: "last_activity",
    className: "whitespace-nowrap",
    render: (value) => (
      <Paragraph size="small" colorType="secondary">
        {value ? format(new Date(value as string), "MMM dd yyyy") : "—"}
      </Paragraph>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    className: "whitespace-nowrap",
    render: (_value, record) => renderStatus(record),
  },
];

export const ExpertChatsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const [providerFilter, setProviderFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<ExpertChatStatusValue[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(EXPERT_CHATS_PAGE_SIZE);
  const [reassignTarget, setReassignTarget] = useState<ExpertChatOversightRow | null>(null);

  const providersQuery = useListExpertChatProviders({
    query: { staleTime: PROVIDER_STALE_TIME_MS, refetchOnWindowFocus: false },
  });
  const providersData = providersQuery.data;
  const providers = useMemo(() => (providersData?.status === 200 ? providersData.data : []), [providersData]);

  const params = {
    search: debouncedSearchTerm || undefined,
    provider_ids: providerFilter.length ? providerFilter.map(Number) : undefined,
    // The backend filters on one status; selecting both means "no filter",
    // which is what picking every option should do anyway.
    status: statusFilter.length === 1 ? statusFilter[0] : undefined,
    page,
    page_size: pageSize,
  };

  const { data, isFetching } = useListExpertChats(params, {
    query: {
      queryKey: ["expert-chats", params],
      placeholderData: keepPreviousData,
    },
  });

  const payload = data?.status === 200 ? data.data : undefined;
  const rows = payload?.results ?? [];
  const totalItemCount = payload?.count ?? 0;

  const providerFilterItems = useMemo(
    () =>
      providers
        .filter((provider) => !provider.archived)
        .map((provider) => ({ id: String(provider.provider_id), label: provider.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [providers],
  );

  const handlePageChange = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
      return;
    }
    setPage(nextPage);
  };

  const getActions = (record: ExpertChatOversightRow) => [
    { label: "View chat", callback: () => navigate(`/expert-chats/${record.chat_id}`) },
    record.status === ExpertChatStatus.OPEN && {
      label: "Reassign",
      callback: () => setReassignTarget(record),
    },
  ];

  return (
    <div className="flex flex-col bg-bg-default min-h-full px-16 py-8">
      <Heading size="medium" className="mb-2">
        Expert Chats
      </Heading>
      <Paragraph size="small" colorType="secondary" className="mb-6">
        Every Frontera Clarity expert chat. Opening one here is read-only — it won&apos;t mark messages as read for the
        assigned expert.
      </Paragraph>

      <Table<ExpertChatOversightRow>
        data={rows}
        columns={COLUMNS}
        loading={isFetching && rows.length === 0}
        showPagination
        currentPageNo={page}
        defaultItemsPerPage={EXPERT_CHATS_PAGE_SIZE}
        itemsPerPageOptions={EXPERT_CHATS_PAGE_SIZE_OPTIONS}
        onPageChange={handlePageChange}
        totalItemCount={totalItemCount}
        actions={getActions}
        onRowClick={(record) => navigate(`/expert-chats/${record.chat_id}`)}
        emptyMessage={searchTerm ? "No expert chats match this search" : "No expert chats found"}
        headerContent={
          <div className="flex w-full items-center justify-between gap-2">
            {/* Own input rather than the Table's `showSearch`: that one re-filters
                `data` against the visible columns, which would drop rows the
                backend matched on a field the table doesn't show — a secondary
                caregiver's name, or the chat type. */}
            <div className="flex flex-row items-center align-middle">
              <MagnifyingGlassIcon className="mr-2 h-5 w-5 text-text-secondary" aria-hidden="true" />
              <input
                type="text"
                name="expert-chat-search"
                aria-label="Search expert chats"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setPage(1);
                }}
                placeholder="Search..."
                className="peer block border-0 py-1.5 text-text-primary placeholder:text-text-secondary focus:ring-0 focus:outline-hidden sm:text-sm sm:leading-6"
              />
            </div>
            <div className="flex items-center gap-2">
              <TableFilterDropdown
                label="Expert"
                items={providerFilterItems}
                value={providerFilter}
                onChange={(next) => {
                  setProviderFilter(next);
                  setPage(1);
                }}
              />
              <TableFilterDropdown<ExpertChatStatusValue>
                label="Status"
                items={STATUS_FILTER_ITEMS}
                value={statusFilter}
                onChange={(next) => {
                  setStatusFilter(next);
                  setPage(1);
                }}
              />
            </div>
          </div>
        }
      />

      <ReassignChatDialog
        isOpen={!!reassignTarget}
        setOpen={(open) => !open && setReassignTarget(null)}
        chat={reassignTarget}
        providers={providers}
        isLoadingProviders={providersQuery.isLoading}
        onReassigned={() => {
          setReassignTarget(null);
          // Reassignment closes the chat and opens a fresh one, so every cached
          // page, filter combination and transcript is out of date — not just
          // the page in view.
          void queryClient.invalidateQueries({ queryKey: ["expert-chats"] });
          void queryClient.invalidateQueries({ queryKey: ["expert-chat-messages"] });
        }}
      />
    </div>
  );
};
